import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Action, Location } from "history";

interface UnsavedChangesPrompt {
  /** True while a blocked navigation is awaiting the user's decision. */
  isPromptOpen: boolean;
  /** Release the block and continue to the pending destination (Discard / post-Save). */
  proceed: () => void;
  /** Dismiss the prompt and remain on the page; the guard stays armed. */
  stay: () => void;
}

/**
 * Guards against losing unsaved work. While `hasUnsavedChanges` is true it
 * blocks in-app navigation (showing a custom prompt via `isPromptOpen`) and
 * triggers the browser's native prompt on tab/window close.
 */
export default function useUnsavedChangesPrompt(
  hasUnsavedChanges: boolean
): UnsavedChangesPrompt {
  const history = useHistory();
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const pendingLocationRef = useRef<Location | null>(null);
  const unblockRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return undefined;
    }

    const unblock = history.block((location: Location, action: Action) => {
      // A REPLACE onto the same path and query keeps this page mounted, so
      // no work is at stake: it only rewrites the entry's state (the academy
      // coachmark dropping its hint). Let it through.
      const { pathname, search } = history.location;
      if (
        action === "REPLACE" &&
        location.pathname === pathname &&
        location.search === search
      ) {
        return undefined;
      }
      pendingLocationRef.current = location;
      setIsPromptOpen(true);
      // Returning false (a non-string) cancels the transition without the
      // native window.confirm, so our custom modal can take over.
      return false;
    });
    unblockRef.current = unblock;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges, history]);

  const proceed = useCallback(() => {
    setIsPromptOpen(false);
    const location = pendingLocationRef.current;
    pendingLocationRef.current = null;
    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }
    if (location) {
      history.push(
        location.pathname + location.search + location.hash,
        location.state
      );
    }
  }, [history]);

  const stay = useCallback(() => {
    setIsPromptOpen(false);
    pendingLocationRef.current = null;
  }, []);

  return { isPromptOpen, proceed, stay };
}
